<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  currentPage: { type: Number, required: true },
  totalItems: { type: Number, required: true },
  itemsPerPage: { type: Number, default: 25 },
  disabled: Boolean,
});
const emit = defineEmits(['change']);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.totalItems / props.itemsPerPage))
);
const start = computed(() =>
  props.totalItems ? (props.currentPage - 1) * props.itemsPerPage + 1 : 0
);
const end = computed(() =>
  Math.min(props.currentPage * props.itemsPerPage, props.totalItems)
);
const pages = computed(() => {
  const total = totalPages.value;
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  if (props.currentPage <= 4) return [1, 2, 3, 4, 5, 'end-gap', total];
  if (props.currentPage >= total - 3)
    return [1, 'start-gap', total - 4, total - 3, total - 2, total - 1, total];
  return [
    1,
    'start-gap',
    props.currentPage - 1,
    props.currentPage,
    props.currentPage + 1,
    'end-gap',
    total,
  ];
});
const canPrevious = computed(() => props.currentPage > 1 && !props.disabled);
const canNext = computed(
  () => props.currentPage < totalPages.value && !props.disabled
);
function change(page) {
  if (
    props.disabled ||
    page === props.currentPage ||
    page < 1 ||
    page > totalPages.value
  )
    return;
  emit('change', page);
}
</script>

<template>
  <footer
    class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak px-3 py-3"
  >
    <p class="text-sm text-n-slate-11 m-0" role="status" aria-live="polite">
      Показано {{ start }}–{{ end }} из {{ totalItems }}
    </p>
    <nav class="flex items-center gap-1.5" aria-label="Страницы размещений">
      <Button
        icon="i-lucide-chevrons-left"
        ghost
        slate
        sm
        class="!size-8 hidden sm:inline-flex"
        :disabled="!canPrevious"
        aria-label="Первая страница"
        title="Первая страница"
        @click="change(1)"
      />
      <Button
        icon="i-lucide-chevron-left"
        ghost
        slate
        sm
        class="!size-8"
        :disabled="!canPrevious"
        aria-label="Предыдущая страница"
        title="Предыдущая страница"
        @click="change(currentPage - 1)"
      />
      <span class="sm:hidden min-w-14 text-center text-sm text-n-slate-11">
        {{ currentPage }} / {{ totalPages }}
      </span>
      <template v-for="page in pages" :key="page">
        <span
          v-if="typeof page === 'string'"
          class="hidden sm:inline-flex size-8 items-center justify-center text-sm text-n-slate-11"
          aria-hidden="true"
        >
          …
        </span>
        <Button
          v-else
          xs
          outline
          class="!size-8 hidden sm:inline-flex"
          :color="page === currentPage ? 'blue' : 'slate'"
          :disabled="disabled"
          :aria-label="`Страница ${page}`"
          :aria-current="page === currentPage ? 'page' : undefined"
          @click="change(page)"
        >
          <span :class="{ 'text-n-brand': page === currentPage }">
            {{ page }}
          </span>
        </Button>
      </template>
      <Button
        icon="i-lucide-chevron-right"
        ghost
        slate
        sm
        class="!size-8"
        :disabled="!canNext"
        aria-label="Следующая страница"
        title="Следующая страница"
        @click="change(currentPage + 1)"
      />
      <Button
        icon="i-lucide-chevrons-right"
        ghost
        slate
        sm
        class="!size-8 hidden sm:inline-flex"
        :disabled="!canNext"
        aria-label="Последняя страница"
        title="Последняя страница"
        @click="change(totalPages)"
      />
    </nav>
  </footer>
</template>
